import { useNavigate } from "react-router-dom";

import { CustomButton, Text } from "../../../shared/uiComponents";
import { PRIMARY } from "../../../shared/Helpers/colors";
import { formatClientName, getClientAge, getInitials } from "../../../shared/Helpers/functions";
import { BtClient } from "../../../redux/State/clientSlice/dashboardSlice";
import { MyClientsSvg } from "../../../shared/Assets/svgIcons";

const DAY_ABBREVIATIONS: Record<number, string> = {
  0: "Su",
  1: "M",
  2: "T",
  3: "W",
  4: "Th",
  5: "F",
  6: "Sa",
};

const formatScheduleDays = (days: number[]): string => {
  if (!days || days.length === 0) return "—";
  return days.map((d) => DAY_ABBREVIATIONS[d] ?? "").join("/");
};

interface MyClientsWidgetProps {
  clients: BtClient[];
}

const MyClientsWidget = ({ clients }: MyClientsWidgetProps) => {
  const navigate = useNavigate();

  if (clients.length === 0) return null;

  return (
    <div className="btMyClientsSection">
      <div className="btSectionHeader btMyClientsHeader">
        <div className="btSectionHeaderLeft">
          <MyClientsSvg />
          <Text
            title="My Clients"
            type="h6"
            font="header"
            fontWeight="semiBold"
          />
          <span className="btSectionCount">
            {clients.length} client{clients.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="btSectionHeaderRight">
          <CustomButton
            className="btHeaderLink"
            title="View All Clients"
            buttonType="link"
            sizeType="sm"
            onClick={() => navigate("/clients")}
          />
        </div>
      </div>
      <div className="btClientsList">
        {clients.map((client) => (
          <ClientRow key={client.client.id} client={client} />
        ))}
      </div>
    </div>
  );
};

const ClientRow = ({ client }: { client: BtClient }) => {
  const navigate = useNavigate();

  const age = getClientAge(client.client.dateOfBirthday);
  const ageLabel = age == null ? "" : `Age ${age}`;
  const sessionsLabel = `${client.sessionsPerWeek} session${client.sessionsPerWeek !== 1 ? "s" : ""}/week`;
  const subtitle = [ageLabel, sessionsLabel].filter(Boolean).join(" • ");

  return (
    <div className="btClientRow">
      <div className="btClientRowLeft">
        <div className="btClientAvatar">{getInitials(client.client.fullName)}</div>
        <div className="btClientInfo">
          <Text className="btClientName" title={formatClientName(client.client.fullName, client.client.isElc)} type="psm" fontWeight="semiBold" />
          <span className="btClientSubtitle">{subtitle}</span>
        </div>
      </div>
      <div className="btClientRowStats">
        <div className="btClientStat">
          <span className="btClientStatValue btClientStatValueCount">{client.activeTargetsCount}</span>
          <span className="btClientStatLabel">TARGETS</span>
        </div>
        <div className="btClientStat">
          <span className="btClientStatValue btClientStatValueSchedule">{formatScheduleDays(client.scheduleDays)}</span>
          <span className="btClientStatLabel">SCHEDULE</span>
        </div>
      </div>
      <div className="btClientRowActions">
        <CustomButton
          title="View Goals"
          sizeType="sm"
          buttonType="outlined"
          onClick={() => navigate(`/clients/client/${client.client.id}`)}
          style={{ borderColor: PRIMARY.BASE, color: PRIMARY.BASE, background: "transparent" }}
        />
        <CustomButton
          title="Manage Schedule"
          sizeType="sm"
          buttonType="outlined"
          onClick={() => navigate("/calendar")}
          style={{ borderColor: PRIMARY.BASE, color: PRIMARY.BASE, background: "transparent" }}
        />
      </div>
    </div>
  );
};

export default MyClientsWidget;
